import { useState } from "react";
import { ArrowLeft, Phone, MessageCircle, Mail, Lock, ShieldCheck, ChevronDown, RefreshCw, Footprints, HeartHandshake, Users, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useProto } from "../ProtoContext";
import { PEOPLE, LEADERS, leaderName } from "../data";
import { Avatar, Pill, fullName, nextText } from "../ui";
import { RhythmAvatar } from "../Rhythm";
import { cn } from "@/lib/utils";

const ICONS = { phone: Phone, text: MessageCircle, email: Mail, visit: Footprints, in_person: Users, need: Sparkles, note: HeartHandshake };
const VERB = { phone: "Phone call", text: "Text", email: "Email", visit: "Visit", in_person: "In person", need: "Care started", note: "Note" };

const Header = ({ p }) => {
  const { back, openLog, status } = useProto();
  const s = status[p.status];
  const reach = (how) => toast(`${how} ${p.first}`, { description: "In the real app this opens your phone or email." });
  const circle = "btn btn-icon";
  return (
    <div>
      <button onClick={back} className="h-11 -ml-2 px-2 inline-flex items-center gap-2 text-ink-soft hover:text-ink" data-testid="person-back-btn"><ArrowLeft size={20} /> Back</button>
      <div className="flex flex-col items-center text-center mt-4">
        <RhythmAvatar person={p} size={136} />
        <h1 className="t-name-xl mt-6" data-testid="person-name">{fullName(p)}</h1>
        <p className="t-body text-ink-soft text-[17px] mt-2" data-testid="person-household-line">{p.age} · {p.household.name}</p>
        <div className="mt-7 w-full grid grid-cols-2 border-y border-line divide-x divide-line" data-testid="person-care-facts">
          <div className="px-4 py-4">
            <p className="t-label">Last connected</p>
            <p className="t-body text-[15px] mt-1.5" data-testid="person-last-connected">{lastParts(p)}</p>
          </div>
          <div className="px-4 py-4 flex flex-col items-center">
            <p className="t-label">Care</p>
            <Pill tone={s.tone} className="mt-2" data-testid="person-status">{s.label}</Pill>
          </div>
        </div>
        <div className="flex gap-3 mt-7">
          <button className={circle} onClick={() => reach("Calling")} aria-label="Call" data-testid="person-call-btn"><Phone size={20} /></button>
          <button className={circle} onClick={() => reach("Texting")} aria-label="Text" data-testid="person-text-btn"><MessageCircle size={20} /></button>
          <button className={circle} onClick={() => reach("Emailing")} aria-label="Email" data-testid="person-email-btn"><Mail size={20} /></button>
        </div>
        <button onClick={() => openLog(p.id)} className="btn btn-primary btn-lg mt-5 w-full" data-testid="person-log-care-btn">Log care</button>
      </div>
    </div>
  );
};

const lastParts = (p) => p.lastSeen.replace(/,? (\d+ days ago|yesterday|today)$/, " · $1");

const NextStep = ({ p }) => (
  <div className="mt-10 pt-6 border-t border-line" data-testid="person-next-step">
    <p className="t-label">What happens next</p>
    <p className="t-heading mt-2">{p.next.label || "Stay connected"}</p>
    <div className="flex flex-wrap items-center gap-3 mt-3">
      <Pill tone={p.status === "paused" ? "later" : p.next.tone}>{nextText(p)}</Pill>
      <span className="owner"><Avatar person={LEADERS[p.caregiver]} size={26} /><span><b>{leaderName(p.caregiver)}</b> owns the next step</span></span>
    </div>
    {p.alsoCaring && <p className="t-meta mt-3">Also caring: {p.alsoCaring}</p>}
  </div>
);

const Need = ({ p }) =>
  p.need ? (
    <div className="mt-10" data-testid="person-need">
      <p className="t-label mb-3">Why {p.first} needs care</p>
      <div className="thread thread-active">
        <p className="t-heading-sm">{p.need.title}</p>
        <p className="t-body mt-1">{p.reason}</p>
        <p className="t-meta mt-2">{p.need.opened}</p>
      </div>
    </div>
  ) : null;

const Member = ({ person, name, rel, self }) => (
  <div className="flex items-center gap-3">
    <Avatar person={person || { name }} size={44} className={self ? "ring-2 ring-brand-soft" : ""} />
    <div><p className="t-name-sm">{name}</p><p className="t-meta">{rel}</p></div>
  </div>
);

const Household = ({ p }) => (
  <div className="tint p-5 mt-10" data-testid="person-household">
    <div className="flex items-baseline justify-between gap-3">
      <p className="t-heading-sm">The {p.last} household</p>
      {p.pco && <span className="text-xs text-ink-faint inline-flex items-center gap-1 shrink-0"><RefreshCw size={12} /> Planning Center</span>}
    </div>
    <div className="mt-4 flex flex-col gap-3 border-l border-line ml-[21px] pl-0">
      <div className="-ml-[22px] flex flex-col gap-3">
        <Member person={p} name={fullName(p)} rel="This profile" self />
        {p.household.members.map((m) => <Member key={m.name} name={m.name} rel={m.rel} />)}
      </div>
    </div>
    {!p.household.members.length && <p className="t-body text-ink-soft text-[15px] mt-3">{p.first} lives alone. No other household members are listed.</p>}
  </div>
);

const Story = ({ p }) => (
  <ol className="relative mt-2" data-testid="person-timeline">
    {!(p.timeline || []).length && <li className="text-ink-soft" data-testid="person-timeline-empty">{p.first}'s story starts with the next care you log.</li>}
    {(p.timeline || []).map((t, i) => {
      const Icon = ICONS[t.type];
      return (
        <li key={i} className="relative pl-14 pb-8 last:pb-0">
          {i < (p.timeline || []).length - 1 && <span className="absolute left-[21px] top-11 bottom-1 w-px bg-line" />}
          <span className="absolute left-0 top-0 h-11 w-11 rounded-full bg-surface border border-line flex items-center justify-center text-ink-soft"><Icon size={18} /></span>
          <p className="t-meta pt-0.5">{VERB[t.type]} · {t.who} · {t.when}</p>
          <p className="t-body text-[17px] leading-relaxed mt-1.5">{t.text}</p>
        </li>
      );
    })}
  </ol>
);

const Confidential = ({ p }) => {
  const { role } = useProto();
  if (role !== "pastor") {
    return (
      <div className="private p-6" data-testid="confidential-locked">
        <Lock className="text-private" size={22} />
        <p className="t-heading-sm mt-3">Confidential notes are private</p>
        <p className="t-body text-ink-soft mt-1">Only pastors with confidential access can see them. Your care notes stay on {p.first}'s story.</p>
      </div>
    );
  }
  return (
    <div data-testid="confidential-content">
      <div className="flex gap-3 text-private text-[15px] font-medium" data-testid="confidential-banner">
        <ShieldCheck size={20} className="shrink-0 mt-0.5" />
        <span>Visible to you and 2 pastors. Opening and editing these notes is recorded.</span>
      </div>
      {p.confidential?.length ? p.confidential.map((c, i) => (
        <div key={i} className="private p-5 mt-4"><p className="t-meta">{c.who} · {c.when}</p><p className="t-body text-[17px] leading-relaxed mt-2">{c.text}</p></div>
      )) : <p className="t-body text-ink-soft mt-4">No confidential notes for {p.first}.</p>}
      <button onClick={() => toast("Add confidential note", { description: "Opens a private note. It never appears on Today, in emails or in search." })} className="btn btn-outline mt-4" data-testid="confidential-add-btn">Add confidential note</button>
    </div>
  );
};

const Tabs = ({ p }) => {
  const [tab, setTab] = useState("story");
  const t = (id, label, Icon) => (
    <button onClick={() => setTab(id)} aria-selected={tab === id} role="tab" className="tab" data-testid={`person-tab-${id}`}>
      {Icon && <Icon size={15} />}{label}
    </button>
  );
  return (
    <div className="mt-12">
      <div className="tabs" role="tablist">{t("story", `${p.first}'s story`)}{t("confidential", "Confidential", Lock)}</div>
      <div className="mt-6">{tab === "story" ? <Story p={p} /> : <Confidential p={p} />}</div>
    </div>
  );
};

const PcoDetails = ({ p }) => {
  const [open, setOpen] = useState(false);
  if (!p.pco) return null;
  return (
    <div className="mt-10 border-t border-line pt-4">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-2 text-ink-soft" data-testid="pco-details-toggle">
        <span className="inline-flex items-center gap-2 text-[15px]"><RefreshCw size={15} /> From Planning Center</span>
        <ChevronDown size={18} className={cn("transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <dl className="grid grid-cols-[110px_1fr] gap-y-2 text-[15px] mt-2" data-testid="pco-details">
          <dt className="text-ink-faint">Phone</dt><dd className="text-ink">{p.phone}</dd>
          <dt className="text-ink-faint">Email</dt><dd className="text-ink break-all">{p.pco.email}</dd>
          <dt className="text-ink-faint">Address</dt><dd className="text-ink">{p.pco.address}</dd>
          <dt className="text-ink-faint">Birthday</dt><dd className="text-ink">{p.pco.birthday}</dd>
          <dt className="text-ink-faint">Church</dt><dd className="text-ink">{p.pco.since}</dd>
          <dd className="col-span-2 text-sm text-ink-faint mt-2">Synced {p.pco.synced}. Edit contact details in Planning Center.</dd>
        </dl>
      )}
    </div>
  );
};

export default function Person() {
  const { personId, isPhone } = useProto();
  const p = PEOPLE[personId];
  if (isPhone) {
    return (
      <div className="px-5 pt-4 pb-36" data-testid="person-screen">
        <Header p={p} /><Need p={p} /><NextStep p={p} /><Tabs p={p} /><Household p={p} /><PcoDetails p={p} />
      </div>
    );
  }
  return (
    <div className="max-w-6xl mx-auto px-10 pt-10 pb-24 grid grid-cols-[380px_1fr] gap-14" data-testid="person-screen">
      <aside><Header p={p} /><Household p={p} /><PcoDetails p={p} /></aside>
      <main className="pt-12"><Need p={p} /><NextStep p={p} /><Tabs p={p} /></main>
    </div>
  );
}
